<script>
	import { createEventDispatcher } from "svelte";
	import { canEditIssue, formatDateTime } from "src/utils/misc";
	import { navigate } from "svelte-routing";
	import { Offcanvas } from "@sveltestrap/sveltestrap";
	import CustomButton from "../../../../components/CustomButton/CustomButton.svelte";
	import Lightbox from "../../../../components/Lightbox/Lightbox.svelte";
	import {
		imageLightbox,
		imageLightboxCaptions,
		toastMessage,
	} from "src/store/store";
	import { statusBadgeStyle } from "src/utils/statusBadge";
	import { issueStatusOptions } from "src/utils/options";
	import Loader from "src/components/Loader/Loader.svelte";
	import { setIssuesHandoverFlag } from "src/services/maintenanceRepair";
	import { isMnrPhase2Enabled } from "src/config/flag-config";

	export let isOpen = false;
	export let toggle = () => {};
	export let issue = null;
	export let isWshop = false;
	export let isLoading = false;
	export let isNotFound = false;
	export let errorMessage = "";
	export let onRetry = null;
	export let vehicleId = undefined;
	export let returnTo = undefined;

	const dispatch = createEventDispatcher();
	let isTogglingFlag = false;

	// Terminal statuses can't be flagged for handover (FRNT-6397).
	const CLOSED_ISSUE_STATUSES = ["Completed", "Cancelled"];
	$: isClosedIssue = CLOSED_ISSUE_STATUSES.includes(issue?.status);

	const issueStatusLabelByValue = Object.fromEntries(
		issueStatusOptions.map((option) => [option.value, option.label])
	);
	const issueStatusVariantByLabel = Object.fromEntries(
		issueStatusOptions.map((option) => [option.label, option.variant])
	);

	$: statusLabel = issue
		? issueStatusLabelByValue[issue.status] || issue.status
		: "";

	$: sortedMedia = issue?.media
		? [...issue.media].sort((a, b) => a.sortOrder - b.sortOrder)
		: [];

	$: sortedPhotos = sortedMedia.filter((item) => item.mediaType !== "video");

	let lightboxOpen = false;
	let lightboxIndex = 0;

	function handleOpenImage(index) {
		lightboxIndex = index;
		imageLightbox.set(sortedMedia.map((item) => item.s3Url));
		imageLightboxCaptions.set([]);
		lightboxOpen = true;
	}

	async function handleFlagToggle() {
		if (!issue || isTogglingFlag) return;
		const issueId = issue.id;
		const newFlagged = !issue.handoverFlagged;
		isTogglingFlag = true;
		const res = await setIssuesHandoverFlag([issueId], newFlagged);
		isTogglingFlag = false;

		if (res.error) {
			const rejected = res.error.rejected?.find(
				(entry) => entry.issueId === issueId
			);
			toastMessage.set({
				type: "danger",
				message:
					rejected?.reason ||
					res.error.message ||
					"Something went wrong. Please try again.",
			});
			return;
		}

		const rejected = res.data?.rejected?.find(
			(entry) => entry.issueId === issueId
		);
		if (rejected) {
			toastMessage.set({
				type: "danger",
				message: rejected.reason || "Couldn't update the flag.",
			});
			return;
		}

		dispatch("flagToggled", {
			issueId,
			handoverFlagged: newFlagged,
		});
		toastMessage.set({
			type: "success",
			message: newFlagged ? "Issue flagged." : "Issue unflagged.",
		});
	}

	function handleEditIssue() {
		const query = returnTo
			? `?returnTo=${encodeURIComponent(returnTo)}`
			: "";
		navigate(
			`/maintenance-repair/${vehicleId ?? issue.vehicleId}/issues/${issue.id}/edit${query}`
		);
	}
</script>

{#if lightboxOpen}
	<Lightbox
		videoFrameThumbnails
		imageShowingIndex={lightboxIndex}
		on:click={() => (lightboxOpen = false)}
	/>
{/if}

<Offcanvas {isOpen} {toggle} placement="end" class="issue-detail-panel">
	<div slot="header" class="issue-detail-title">
		{#if issue}
			<h2>{issue.code}</h2>
			{#if !isWshop && isMnrPhase2Enabled && issue.handoverFlagged}
				<img
					src="/assets/icon-alert-filled-red.svg"
					alt="Flagged"
					class="issue-detail-flag-icon"
				/>
			{/if}
		{/if}
	</div>

	{#if isLoading}
		<Loader />
	{:else if isNotFound}
		<span class="issue-detail-value">This issue no longer exists.</span>
	{:else if errorMessage}
		<div class="issue-detail-error" role="alert">
			<span class="issue-detail-value">Couldn't load this issue.</span>
			<span class="issue-detail-label">{errorMessage}</span>
			{#if onRetry}
				<div>
					<CustomButton
						buttonText="Retry"
						color="secondary"
						on:click={onRetry}
					/>
				</div>
			{/if}
		</div>
	{:else if issue}
		{#if isWshop}
			<div class="issue-detail-row">
				<div class="issue-detail-field">
					<span class="issue-detail-label">Issue type</span>
					<span class="issue-detail-value"
						>{issue.issueType?.name}</span
					>
				</div>
				<div class="issue-detail-field">
					<span class="issue-detail-label">Service category</span>
					<span class="issue-detail-value"
						>{issue.serviceCategory}</span
					>
				</div>
			</div>

			<div class="issue-detail-row">
				<div class="issue-detail-field issue-detail-field-full">
					<span class="issue-detail-label">Issue description</span>
					<span class="issue-detail-value"
						>{issue.issueDescription}</span
					>
				</div>
			</div>

			<div class="issue-detail-row">
				<div class="issue-detail-field">
					<span class="issue-detail-label"
						>Date and time reported</span
					>
					<span class="issue-detail-value"
						>{formatDateTime(issue.createdAt)}</span
					>
				</div>
			</div>
		{:else}
			<div class="issue-detail-row">
				<div class="issue-detail-field">
					<span class="issue-detail-label">Service category</span>
					<span class="issue-detail-value"
						>{issue.serviceCategory}</span
					>
				</div>
				<div class="issue-detail-field">
					<span class="issue-detail-label">Issue type</span>
					<span class="issue-detail-value"
						>{issue.issueType?.name}</span
					>
				</div>
			</div>

			<div class="issue-detail-row">
				<div class="issue-detail-field issue-detail-field-full">
					<span class="issue-detail-label">Issue description</span>
					<span class="issue-detail-value"
						>{issue.issueDescription}</span
					>
				</div>
			</div>

			<div class="issue-detail-row">
				<div class="issue-detail-field">
					<span class="issue-detail-label">Status</span>
					<span
						class="status-badge"
						style={statusBadgeStyle(
							issueStatusVariantByLabel[statusLabel]
						)}
					>
						{statusLabel.toUpperCase()}
					</span>
				</div>
				<div class="issue-detail-field">
					<span class="issue-detail-label">Added by</span>
					<span class="issue-detail-value">{issue.createdBy}</span>
				</div>
			</div>

			<div class="issue-detail-row">
				<div class="issue-detail-field">
					<span class="issue-detail-label"
						>Date and time reported</span
					>
					<span class="issue-detail-value"
						>{formatDateTime(issue.createdAt)}</span
					>
				</div>
				<div class="issue-detail-field">
					<span class="issue-detail-label">Zendesk ID</span>
					<span class="issue-detail-value"
						>{issue.zendeskId || "-"}</span
					>
				</div>
			</div>

			<div class="issue-detail-row">
				{#if isMnrPhase2Enabled}
					<div class="issue-detail-field">
						<span class="issue-detail-label">Work order</span>
						<span class="issue-detail-value"
							>{issue.workOrder?.code || "-"}</span
						>
					</div>
				{/if}
				<div class="issue-detail-field">
					<span class="issue-detail-label">Related case</span>
					<span class="issue-detail-value"
						>{issue.relatedCaseNo || "-"}</span
					>
				</div>
			</div>
		{/if}

		<div class="issue-detail-images">
			<span class="issue-detail-label">Images & Videos</span>
			{#if sortedMedia.length}
				<div class="issue-detail-images-grid">
					{#each sortedMedia as item, index (item.id)}
						<button
							type="button"
							class="issue-detail-image-button"
							on:click={() => handleOpenImage(index)}
						>
							<span class="visually-hidden">
								{`${
									item.mediaType === "video"
										? "Play video"
										: "View image"
								} ${index + 1} of ${sortedMedia.length}`}
							</span>
							{#if item.mediaType === "video"}
								<!-- #t=0.1 makes the browser seek, so a frame
								     actually paints instead of a black box. -->
								<video
									src={`${item.s3Url}#t=0.1`}
									class="issue-detail-image"
									muted
									playsinline
									preload="metadata"
								>
									<track kind="captions" />
								</video>
								<span class="issue-detail-play-badge">
									<svg viewBox="0 0 24 24" aria-hidden="true">
										<circle
											cx="12"
											cy="12"
											r="11"
											fill="rgba(0, 0, 0, 0.55)"
										/>
										<path
											d="M9.5 7.5v9l7-4.5z"
											fill="#fff"
										/>
									</svg>
								</span>
							{:else}
								<img
									src={item.s3Url}
									alt=""
									class="issue-detail-image"
								/>
							{/if}
						</button>
					{/each}
				</div>
			{:else}
				<span class="issue-detail-value">No images uploaded</span>
			{/if}
		</div>

		{#if issue.unfixedReason?.trim()}
			<hr class="issue-detail-divider" />

			<div class="issue-detail-field">
				<span class="issue-detail-label"
					>Reason for not resolving issue</span
				>
				<span class="issue-detail-value">{issue.unfixedReason}</span>
			</div>
		{/if}

		{#if issue.status === "Cancelled" && issue.cancelReason?.label}
			<hr class="issue-detail-divider" />

			<div class="issue-detail-field">
				<span class="issue-detail-label">Reason for cancelling</span>
				<span class="issue-detail-value"
					>{issue.cancelReason.label}</span
				>
			</div>
		{/if}

		{#if !isWshop}
			<div class="issue-detail-actions">
				{#if isMnrPhase2Enabled && !isClosedIssue}
					<CustomButton
						buttonText={issue.handoverFlagged
							? "Unflag issue"
							: "Flag issue"}
						color="secondary"
						on:click={handleFlagToggle}
					/>
				{/if}
				{#if canEditIssue(issue)}
					<CustomButton
						buttonText="Edit issue"
						color="secondary"
						on:click={handleEditIssue}
					/>
				{/if}
			</div>
		{/if}
	{/if}
</Offcanvas>

<style>
	:global(.issue-detail-panel) {
		width: 516px !important;
		padding: 40px 24px;
	}

	:global(.issue-detail-panel .offcanvas-body) {
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: 24px;
	}

	:global(.issue-detail-panel .offcanvas-header) {
		padding: 0;
		margin-bottom: 32px;
	}

	.issue-detail-title {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.issue-detail-title h2 {
		font-size: 24px;
		font-weight: bold;
		margin: 0;
		color: var(--fds-on-surface, #00071f);
	}

	.issue-detail-flag-icon {
		width: 24px;
		height: 24px;
	}

	.issue-detail-row {
		display: flex;
		gap: 16px;
		width: 100%;
	}

	.issue-detail-field {
		flex: 1 1 0;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.issue-detail-field-full {
		flex: 1 1 100%;
	}

	.issue-detail-label {
		font-size: 14px;
		color: var(--fds-on-surface-variant, #595959);
	}

	.issue-detail-value {
		font-size: 14px;
		color: var(--fds-on-surface, #00071f);
		overflow-wrap: anywhere;
	}

	.status-badge {
		display: inline-block;
		width: fit-content;
		border-radius: 4px;
		padding: 4px 8px;
		font-size: 12px;
		font-weight: bold;
	}

	.issue-detail-images {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.issue-detail-images-grid {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}

	.issue-detail-image {
		width: 100px;
		height: 100px;
		object-fit: cover;
		border-radius: 4px;
	}

	.issue-detail-play-badge {
		position: absolute;
		top: 50%;
		left: 50%;
		width: 32px;
		height: 32px;
		transform: translate(-50%, -50%);
		pointer-events: none;
	}

	.issue-detail-image-button {
		position: relative;
		display: block;
		padding: 0;
		border: none;
		background: none;
		cursor: pointer;
	}

	.issue-detail-error {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.issue-detail-divider {
		width: 100%;
		margin: 0;
		border: none;
		border-top: 1px solid #d3d3d3;
	}

	.issue-detail-actions {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
	}

	.issue-detail-actions :global(.fds-button.btn-secondary) {
		border: 1px solid #d3d3d3;
	}
</style>
